import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getAdminOverview } from "@/lib/admin.functions";
import { getDashboardExtras } from "@/lib/admin-extra.functions";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  CalendarDays,
  Wallet,
  Users,
  TrendingUp,
  AlertCircle,
  ShoppingBag,
  Receipt,
  FileText,
  ClipboardList,
} from "lucide-react";
import { formatAppointmentLabel } from "@/lib/booking-helpers";

export const Route = createFileRoute("/_authenticated/admin/")({
  component: OverviewPage,
});

function rand(n: number) { return "R" + n.toLocaleString("en-ZA"); }

function OverviewPage() {
  const fn = useServerFn(getAdminOverview);
  const extrasFn = useServerFn(getDashboardExtras);
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => fn(),
  });
  const { data: extras } = useQuery({
    queryKey: ["admin", "dashboard-extras"],
    queryFn: () => extrasFn(),
  });

  if (isPending) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (isError) return <p className="text-sm text-destructive">Failed to load: {(error as Error).message}</p>;
  const t = data.totals;

  const maxDaily = Math.max(1, ...data.last30.map((d) => d.revenue));
  const maxMonthly = Math.max(1, ...data.monthly.map((d) => d.revenue));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Practice at a glance</h2>
          <p className="text-sm text-muted-foreground">Live snapshot of bookings, revenue and the shop.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm" variant="outline">
            <Link to="/admin/bookings"><CalendarDays className="mr-2 h-4 w-4" />Bookings</Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to="/admin/products"><ShoppingBag className="mr-2 h-4 w-4" />Products</Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to="/admin/invoices"><FileText className="mr-2 h-4 w-4" />New invoice</Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to="/admin/reports"><ClipboardList className="mr-2 h-4 w-4" />Reports</Link>
          </Button>
        </div>
      </div>


      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard icon={CalendarDays} label="Bookings today" value={String(t.bToday)} sub={`${t.bWeek} this week`} />
        <MetricCard icon={CalendarDays} label="Bookings this month" value={String(t.bMonth)} sub={`${t.bYear} this year`} />
        <MetricCard icon={Wallet} label="Revenue this month" value={rand(t.rMonth)} sub={`${rand(t.rYear)} YTD`} />
        <MetricCard icon={Users} label="Patients (30d new)" value={String(t.newPatients)} sub={`${t.returningPatients} returning`} />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard icon={Wallet} label="Revenue today" value={rand(t.rToday)} sub={`${rand(t.rWeek)} this week`} />
        <MetricCard icon={TrendingUp} label="Avg daily (YTD)" value={rand(t.avgDaily)} />
        <MetricCard icon={AlertCircle} label="Outstanding payments" value={String(t.outstanding)} sub="bookings unpaid" />
        <MetricCard icon={AlertCircle} label="Cancellations" value={String(t.cancelled)} sub="all time" />
      </div>

      {extras && (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <MetricCard icon={Receipt} label="Shop orders" value={String(extras.orders.total)} sub={`${extras.orders.paid} paid`} />
            <MetricCard icon={Wallet} label="Shop revenue" value={rand(extras.orders.revenue)} sub="paid orders" />
            <MetricCard icon={AlertCircle} label="Awaiting payment" value={String(extras.orders.awaiting)} sub="shop orders" />
            <MetricCard icon={CalendarDays} label="Appointments today" value={String(extras.todaysAppointments.length)} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle>Today's schedule</CardTitle></CardHeader>
              <CardContent>
                {extras.todaysAppointments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nothing booked for today.</p>
                ) : (
                  <ul className="divide-y">
                    {extras.todaysAppointments.map((a) => (
                      <li key={a.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                        <div className="min-w-0">
                          <p className="truncate font-medium">{a.client_name}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            {a.service} · {formatAppointmentLabel(a.appointment_at)}
                          </p>
                        </div>
                        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs">{a.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Outstanding payments</CardTitle>
                <CardDescription>Bookings not yet marked as paid.</CardDescription>
              </CardHeader>
              <CardContent>
                {extras.pendingPayments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Everything is settled.</p>
                ) : (
                  <ul className="divide-y">
                    {extras.pendingPayments.map((p) => (
                      <li key={p.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                        <div className="min-w-0">
                          <p className="truncate font-medium">{p.client_name}</p>
                          <p className="truncate text-xs text-muted-foreground">{p.service}</p>
                        </div>
                        <span className="shrink-0">{rand(p.amount)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle>Recent shop orders</CardTitle></CardHeader>
              <CardContent>
                {extras.orders.recent.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No orders yet.</p>
                ) : (
                  <ul className="divide-y">
                    {extras.orders.recent.map((o) => (
                      <li key={o.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                        <div className="min-w-0">
                          <p className="truncate font-medium">{o.order_number}</p>
                          <p className="truncate text-xs text-muted-foreground">{o.customer_name}</p>
                        </div>
                        <span className="shrink-0">{rand(Number(o.total ?? 0))}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Recent activity</CardTitle>
                <CardDescription>
                  <Link to="/admin/activity" className="underline">View the full log</Link>
                </CardDescription>
              </CardHeader>
              <CardContent>
                {extras.activity.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No activity recorded yet.</p>
                ) : (
                  <ul className="divide-y">
                    {extras.activity.map((a) => (
                      <li key={a.id} className="py-2 text-sm">
                        <p className="font-medium">{a.action}</p>
                        <p className="text-xs text-muted-foreground">
                          {a.actor_email ?? "system"} ·{" "}
                          {new Date(a.created_at).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}



      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Revenue, last 30 days</CardTitle>
            <CardDescription>Daily revenue based on booked appointments.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex h-40 items-end gap-1">
              {data.last30.map((d) => (
                <div key={d.date} className="group relative flex-1" title={`${d.date}: ${rand(d.revenue)}`}>
                  <div
                    className="w-full rounded-t bg-primary/80 transition-colors group-hover:bg-primary"
                    style={{ height: `${(d.revenue / maxDaily) * 100}%`, minHeight: d.revenue > 0 ? 2 : 0 }}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Revenue, this year</CardTitle>
            <CardDescription>Monthly totals.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex h-40 items-end gap-2">
              {data.monthly.map((m) => (
                <div key={m.month} className="flex flex-1 flex-col items-center gap-1">
                  <div className="w-full flex-1 flex items-end">
                    <div
                      className="w-full rounded-t bg-secondary"
                      style={{ height: `${(m.revenue / maxMonthly) * 100}%`, minHeight: m.revenue > 0 ? 2 : 0 }}
                    />
                  </div>
                  <span className="text-[10px] text-muted-foreground">{m.month}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Upcoming appointments</CardTitle></CardHeader>
          <CardContent>
            {data.upcoming.length === 0 ? (
              <p className="text-sm text-muted-foreground">No upcoming appointments.</p>
            ) : (
              <ul className="divide-y">
                {data.upcoming.map((u) => (
                  <li key={u.id} className="flex items-center justify-between py-2 text-sm">
                    <div>
                      <p className="font-medium">{u.service}</p>
                      <p className="text-xs text-muted-foreground">{formatAppointmentLabel(u.appointment_at)}</p>
                    </div>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{u.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Top services</CardTitle></CardHeader>
          <CardContent>
            {data.topServices.length === 0 ? (
              <p className="text-sm text-muted-foreground">No data yet.</p>
            ) : (
              <ul className="space-y-2">
                {data.topServices.map((s) => (
                  <li key={s.name} className="text-sm">
                    <div className="flex justify-between"><span>{s.name}</span><span className="text-muted-foreground">{s.count}</span></div>
                    <div className="mt-1 h-1.5 w-full rounded bg-muted">
                      <div className="h-full rounded bg-primary" style={{ width: `${(s.count / data.topServices[0].count) * 100}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub }: { icon: any; label: string; value: string; sub?: string }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        <p className="mt-2 text-2xl font-semibold">{value}</p>
        {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}
